import { useCallback, useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { usePoints } from '../../hooks/usePoints';
import useVoiceInput from '../../hooks/useVoiceInput';
import MicButton from '../voice/MicButton';
import PointsPanel from './PointsPanel';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'user', label: 'You' },
  { key: 'ai', label: 'AI' },
];

const PILL_BASE =
  'px-3 py-1 rounded-full text-xs font-medium transition-colors';

export default function PointsSection({ ideaId }) {
  const { points, loading, addPoint, editPoint, deletePoint, movePoint } =
    usePoints(ideaId);
  const [filter, setFilter] = useState('all');
  const [reordering, setReordering] = useState(false);
  const [draft, setDraft] = useState('');
  const handleTranscript = useCallback((text) => {
    setDraft((d) => (d.trim() ? `${d.trim()} ${text}` : text));
  }, []);
  const voice = useVoiceInput({ onTranscript: handleTranscript });

  const storageKey = `points-open-${ideaId}`;
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(storageKey) !== 'false';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, String(open));
    } catch {
      // Private mode / storage disabled — the default is fine.
    }
  }, [open, storageKey]);

  const handleAdd = (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    voice.stop();
    addPoint(draft);
    setDraft('');
    voice.reset();
  };

  const visible =
    filter === 'all' ? points : points.filter((p) => p.source === filter);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-800">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left"
      >
        <h2 className="font-semibold text-gray-900 dark:text-white">
          Points {points.length > 0 && `(${points.length})`}
        </h2>
        {open ? (
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronRightIcon className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-1">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={`${PILL_BASE} ${
                    filter === f.key
                      ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-400'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {points.length > 1 && (
              <button
                type="button"
                onClick={() => setReordering((v) => !v)}
                className={`${PILL_BASE} ${
                  reordering
                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-400'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                {reordering ? 'Done' : 'Reorder'}
              </button>
            )}
          </div>

          {loading ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">Loading…</p>
          ) : (
            <PointsPanel
              points={visible}
              editable
              reordering={reordering}
              onEdit={editPoint}
              onDelete={deletePoint}
              onMove={movePoint}
              emptyMessage={
                filter === 'all'
                  ? 'No points yet. Develop this idea to start building them.'
                  : `No points from ${filter === 'user' ? 'you' : 'the AI'} yet.`
              }
            />
          )}

          <form onSubmit={handleAdd} className="flex gap-2 items-center pt-2">
            <MicButton
              size="inline"
              supported={voice.supported}
              listening={voice.listening}
              onStart={voice.start}
              onStop={voice.stop}
              className="!rounded-lg !w-9 !h-9 shrink-0"
            />
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Add a point…"
              className="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
            />
            <button
              type="submit"
              disabled={!draft.trim()}
              className="px-3 py-2 text-sm bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
